/**
 * TagSelector - Componente de seleção de tags com UX aprimorada.
 * - Versão compacta para cabeçalho (popover).
 * - Versão inline para modais.
 * - Suporte a dark mode (useTheme).
 * - ESC fecha + body scroll lock.
 * - Posicionamento responsivo (right-align se vazar viewport).
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, Tag as TagIcon, Loader2 } from 'lucide-react';
import { TagsService, CategoriesService, TagResponse, CategoryResponse, TagUtils } from '../services/apiTags.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface TagSelectorProps {
  selectedTags: number[];
  onTagsChange: (tagIds: number[]) => void;
  clinicId: number;
  maxTags?: number;
  readonly?: boolean;
  className?: string;
  placeholder?: string;
  variant?: 'compact' | 'inline';
}

const normalizeCategoryName = (name: string) => name.toLowerCase().trim();

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  clinicId,
  maxTags = 10,
  readonly = false,
  className = '',
  placeholder = 'Gerenciar Tags',
  variant = 'compact'
}) => {
  const { isDark } = useTheme();

  // Estados de Dados
  const [allTags, setAllTags] = useState<TagResponse[]>([]);
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados de UI
  const [activeCategoryKey, setActiveCategoryKey] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(variant === 'inline');
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);

  // Carregar dados iniciais
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tagsData, categoriesData] = await Promise.all([
        TagsService.getTags(clinicId, { is_active: true, limit: 1000 }),
        CategoriesService.getCategoriesWithCount(clinicId)
      ]);

      setAllTags(tagsData);
      setCategories(categoriesData);
    } catch (err: any) {
      console.error('Erro ao carregar dados do TagSelector:', err);
      setError('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    if ((variant === 'inline' || isExpanded) && allTags.length === 0) {
      loadData();
    }
  }, [isExpanded, variant, loadData, allTags.length]);

  // ESC fecha popover
  useEffect(() => {
    if (!isExpanded || variant === 'inline') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isExpanded, variant]);

  // Posicionamento absoluto computado a partir do trigger (usado pelo Portal).
  // Evita problemas de stacking-context ao renderizar via createPortal.
  const handleToggle = () => {
    if (!isExpanded && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popoverWidth = 320; // w-80
      const margin = 16;
      let left: number;
      if (rect.left + popoverWidth > window.innerWidth - margin) {
        left = rect.right - popoverWidth;
      } else {
        left = rect.left;
      }
      setPopoverPos({
        top: rect.bottom + 8,
        left: Math.max(margin, left),
      });
    }
    setIsExpanded(!isExpanded);
  };

  // Categorias automáticas compartilham id=0; o nome normalizado identifica a aba.
  const dedupedCategories = useMemo(() => {
    const seen = new Map<string, CategoryResponse>();
    for (const cat of categories) {
      const key = normalizeCategoryName(cat.name);
      const existing = seen.get(key);
      if (!existing || (existing.id === 0 && cat.id !== 0)) {
        seen.set(key, cat);
      }
    }
    return Array.from(seen, ([key, category]) => ({ ...category, key }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  // A seleção e as tags exibidas usam a mesma chave da conciliação de categorias.
  const visibleTags = useMemo(() => {
    if (activeCategoryKey === null) {
      return allTags;
    }
    return allTags.filter(tag => normalizeCategoryName(tag.category ?? '') === activeCategoryKey);
  }, [activeCategoryKey, allTags]);

  const handleToggleTag = (tagId: number) => {
    if (readonly) return;
    const isSelected = selectedTags.includes(tagId);
    if (isSelected) {
      onTagsChange(selectedTags.filter(id => id !== tagId));
    } else {
      if (selectedTags.length >= maxTags) return;
      onTagsChange([...selectedTags, tagId]);
    }
  };

  const hasReachedMax = selectedTags.length >= maxTags;

  // Renderização do Conteúdo (Abas + Grid)
  const renderContent = () => (
    <div className={variant === 'inline' ? 'w-full' : 'p-3'}>
      {loading ? (
        <div className={`py-8 flex flex-col items-center justify-center gap-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <Loader2 className="w-5 h-5 animate-spin text-sminy-primary" />
          <span className="text-xs font-medium">Carregando tags...</span>
        </div>
      ) : error ? (
        <div className={`py-8 flex flex-col items-center justify-center gap-2 text-center px-4 ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
          <span className="text-xs font-medium">{error}</span>
          <button
            onClick={loadData}
            className="text-xs underline hover:no-underline"
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <>
          {/* Abas de Categorias */}
          <div className="flex gap-1.5 overflow-x-auto pb-3 mb-1 scrollbar-hide px-1">
            <button
              type="button"
              aria-pressed={activeCategoryKey === null}
              onClick={() => setActiveCategoryKey(null)}
              className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all duration-200 ${
                activeCategoryKey === null
                  ? isDark
                    ? 'bg-sminy-primary text-white shadow-md scale-105'
                    : 'bg-sminy-primary text-white shadow-md scale-105'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
            >
              Todas
            </button>
            {dedupedCategories.map(cat => {
              const isActive = activeCategoryKey === cat.key;
              const categoryColor = cat.color || '#6B7280';
              const tagCountInCategory = allTags.filter(t => normalizeCategoryName(t.category ?? '') === cat.key).length;
              return (
                <button
                  key={cat.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveCategoryKey(cat.key)}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-1.5 ${
                    isActive
                      ? 'shadow-md scale-105'
                      : isDark
                        ? 'border border-gray-700 text-gray-400 hover:bg-gray-700/50'
                        : 'border border-gray-100 text-gray-600 hover:bg-gray-50 hover:scale-105'
                  }`}
                  style={
                    isActive
                      ? {
                          backgroundColor: categoryColor,
                          color: TagUtils.getContrastColor(categoryColor),
                          border: 'none'
                        }
                      : {}
                  }
                >
                  {cat.name}
                  {isActive && (
                    <span className="bg-black/15 px-1 rounded text-[9px] min-w-[14px] text-center">
                      {tagCountInCategory}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Grid de Tags */}
          <div className={`${variant === 'inline' ? 'max-h-96' : 'max-h-60'} overflow-y-auto pr-1 custom-scrollbar`}>
            {visibleTags.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-1">
                {visibleTags.map(tag => {
                  const isSelected = selectedTags.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => handleToggleTag(tag.id)}
                      disabled={!isSelected && hasReachedMax}
                      className={`group relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all duration-200 border select-none ${
                        isSelected
                          ? 'shadow-sm ring-1 ring-offset-1 ring-sminy-primary scale-[1.02]'
                          : isDark
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-sminy-primary/50 hover:shadow-sm hover:scale-[1.02]'
                            : 'hover:border-sminy-primary/50 hover:shadow-sm hover:scale-[1.02] bg-white border-gray-200 text-gray-700'
                      } ${!isSelected && hasReachedMax ? 'opacity-40 cursor-not-allowed grayscale' : 'cursor-pointer'}`}
                      style={
                        isSelected
                          ? {
                              backgroundColor: tag.color,
                              color: TagUtils.getContrastColor(tag.color),
                              borderColor: 'transparent',
                              ['--tw-ring-color' as any]: tag.color
                            }
                          : {}
                      }
                    >
                      {isSelected && <Check className="w-3 h-3 animate-in zoom-in duration-300" />}
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={`flex flex-col items-center justify-center py-8 gap-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <TagIcon className="w-5 h-5 opacity-40" />
                </div>
                <span className="text-xs">Nenhuma tag nesta categoria</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className={`w-full ${className}`}>
        {renderContent()}
      </div>
    );
  }

  // Renderização Compacta (Popover)
  return (
    <div className={`inline-flex items-center ${className}`}>
      {!readonly && (
        <div className="relative">
          <button
            ref={triggerRef}
            onClick={handleToggle}
            className={`group inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed transition-all duration-200 ${
              isExpanded
                ? 'bg-sminy-primary/10 border-sminy-primary text-sminy-primary shadow-sm ring-2 ring-sminy-primary/20'
                : isDark
                  ? 'border-gray-600 text-gray-400 hover:border-sminy-primary hover:text-sminy-primary hover:bg-sminy-primary/10'
                  : 'border-gray-300 text-gray-400 hover:border-sminy-primary hover:text-sminy-primary hover:bg-sminy-primary/5'
            }`}
            title={placeholder}
            aria-label={placeholder}
          >
            <Plus className="w-3.5 h-3.5 transition-transform group-hover:scale-110" />
          </button>

          {/* Popover de Seleção (renderizado via Portal para escapar stacking-context) */}
          {isExpanded && createPortal(
            <>
              <div
                className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm"
                onClick={() => setIsExpanded(false)}
              />
              <div
                style={{ top: popoverPos.top, left: popoverPos.left }}
                className={`fixed w-80 rounded-2xl shadow-2xl z-[9999] animate-in fade-in zoom-in-95 duration-200 overflow-hidden border ${
                  isDark
                    ? 'bg-gray-800 border-gray-600'
                    : 'bg-white border-gray-200'
                }`}
              >
                {/* Header do Popover */}
                <div className={`px-4 py-3 border-b flex items-center justify-between ${
                  isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-100 bg-gray-50/50'
                }`}>
                  <span className={`text-xs font-semibold flex items-center gap-1.5 ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    <TagIcon className="w-3.5 h-3.5 text-sminy-primary" />
                    {placeholder}
                  </span>
                  <span className={`text-[10px] font-medium ${
                    isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {selectedTags.length}/{maxTags} selecionadas
                  </span>
                </div>

                {renderContent()}

                {/* Footer do Popover */}
                <div className={`px-4 py-2 border-t text-[10px] text-center ${
                  isDark ? 'bg-gray-900/30 border-gray-700 text-gray-500' : 'bg-gray-50 border-gray-100 text-gray-400'
                }`}>
                  Clique para adicionar ou remover · ESC para fechar
                </div>
              </div>
            </>,
            document.body
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
